import { Menu } from "@headlessui/react";
import clsx from "clsx";

import { FocusRing } from "@/components/Layout/BaseLayouts/Footer/FocusRing";

import { getNavItemStateClasses } from "./navItemStateClasses";

type NavItemButtonProps = {
  buttonHandler: () => void;
  label: string;
  selected: boolean;
  /** Use the inverted (light-on-dark) variant. Default: false. */
  inverted?: boolean;
  /** Disables the item. Default: false. */
  disabled?: boolean;
};

/**
 * Button nav item for use inside a Headless UI Menu.
 * Implements all states (default, hovered, pressed, focused, disabled)
 * and both variants (default, inverted).
 *
 * Must be wrapped in a {@link Menu} component.
 *
 * @example
 * ```jsx
 * <Menu>
 *   <NavItemButton
 *     buttonHandler={handleClick}
 *     selected={false}
 *     label="My Label"
 *     inverted
 *   />
 * </Menu>
 * ```
 *
 * @param buttonHandler - Called when the button is clicked.
 * @param label - Unique label text displayed in the menu.
 * @param selected - Whether the item is in the current/selected state.
 * @param inverted - Use the inverted (light-on-dark) variant. Default: false.
 * @param disabled - Disables the item. Default: false.
 */
export const NavItemButton: React.FC<NavItemButtonProps> = ({
  label,
  buttonHandler,
  selected,
  inverted = false,
  disabled = false,
}) => (
  <Menu.Item key={label} disabled={disabled}>
    {({ active }) => (
      <button
        id={label.toLowerCase().replace(/\s+/g, "-")}
        className={clsx(
          "group relative w-fit cursor-pointer rounded-md p-2 text-base font-semibold focus:outline-none",
          getNavItemStateClasses({ inverted, disabled, active, selected }),
          disabled && "cursor-not-allowed",
        )}
        onClick={buttonHandler}
        disabled={disabled}
      >
        {label}
        <FocusRing inverted={inverted} />
      </button>
    )}
  </Menu.Item>
);
